import { useQuery } from '@tanstack/react-query';
import { getPostProcessingActions } from 'api/postProcessingAction';
import {
  modelTypeBinaryClassifier,
  modelTypeNucleiSegmentation,
  modelTypesByValue,
} from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  labelColumnName,
  markerPositivityColumnPrefix,
  markerProbabilityColumnPrefix,
  quantificationToDisplayName,
} from 'components/Procedure/useSlideChannelsAndResults/parseHeatmaps';
import { NumberOption, OrchestrationData, OrchestrationDataType } from 'interfaces/calculateFeatures';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import { ClusteringResult } from 'interfaces/job';
import { Thresholds } from 'interfaces/jobs/multiplex/cellTypingParams';
import PostProcessingAction, {
  ActionInput,
  InputSource,
  InputType,
  MappingFilterMetadata,
  NewCellColumns,
  PostProcessingActionCreated,
} from 'interfaces/postProcessingAction';
import {
  compact,
  concat,
  Dictionary,
  endsWith,
  filter,
  find,
  findIndex,
  flatMap,
  forEach,
  get,
  groupBy,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isNumber,
  isPlainObject,
  join,
  keyBy,
  keys,
  map,
  omitBy,
  pickBy,
  set,
  size,
  slice,
  some,
  sortBy,
  startCase,
  startsWith,
  uniq,
  uniqWith,
  values,
} from 'lodash';
import { stringify } from 'qs';
import { useMemo } from 'react';
import { getFormattedCell } from 'utils/formatBrackets';
import { ModelPreferencesById } from 'utils/models';
import useTaxonomyTags from 'utils/queryHooks/taxonomy/useTaxonomyTags';
import { useClassificationModelOptions, useFeatureParams } from 'utils/queryHooks/uiConstantsHooks';
import { useAreaTypes } from 'utils/queryHooks/useAreaTypes';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import { useAreaTypeIdToDisplayName } from 'utils/useAreaTypeIdToDisplayName';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import type { NewCell } from '..';
import { RegisteredAreaOption } from '../areaTransfers';
import type { ClassConfigs, OrchestrationMetadata } from '../classConfigFromSelections';
import {
  getProbeDisplayName,
  getSpotDetectionCountBinCellDefinitions,
  getSpotDetectionCountColumns,
  getSpotDetectionPositivityThresholdMinCount,
  getSpotDetectionProbeNamesForStain,
  SPOT_DETECTION_RESULT_NAME,
} from '../spotDetectionInputSetup';
import { combineArrays, getDetectionLabelOptions } from './utils';

const BINNED = 'binned';
const INSTANCE_COUNT_POSITIVITY_ACTION_NAME = 'get_instance_count_positivity_by_thresholding';
const INSTANCE_FEATURE_CELL_OPTION = { id: '__all__', label: 'Cell' };

const getAttributeSourceDisplayName = (sourceName: string) =>
  modelTypesByValue[sourceName]?.text ?? startCase(sourceName);

const getAttributeLabelDisplayName = (sourceName: string, label: string) =>
  `${getAttributeSourceDisplayName(sourceName)} ${startCase(label)}`;

interface CellMapping {
  label: string;
  displayLabel?: string;
  type: OrchestrationDataType;
}

export const useActionsQuery = () => {
  const areasQuery = useQuery({
    queryKey: ['postProcessingActions', stringify({ actionInput: ActionInput.AREA })],
    queryFn: () => getPostProcessingActions(stringify({ actionInput: ActionInput.AREA })),
  });
  const cellsQuery = useQuery({
    queryKey: ['postProcessingActions', stringify({ actionInput: ActionInput.CELL })],
    queryFn: () => getPostProcessingActions(stringify({ actionInput: ActionInput.CELL })),
  });
  const featuresQuery = useQuery({
    queryKey: ['postProcessingActions', stringify({ actionInput: ActionInput.FEATURE_FAMILY })],
    queryFn: () => getPostProcessingActions(stringify({ actionInput: ActionInput.FEATURE_FAMILY })),
  });
  const postProcessingActionsAreas = areasQuery.data;
  const postProcessingActionsCells = cellsQuery.data;
  const postProcessingActionsFeatures = featuresQuery.data;
  const postProcessingActionsById = useMemo(
    () =>
      keyBy(
        [
          ...(postProcessingActionsAreas ?? []),
          ...(postProcessingActionsCells ?? []),
          ...(postProcessingActionsFeatures ?? []),
        ],
        'id'
      ),
    [postProcessingActionsAreas, postProcessingActionsCells, postProcessingActionsFeatures]
  );
  return {
    postProcessingActionsById,
    postProcessingActionsAreas,
    postProcessingActionsCells,
    postProcessingActionsFeatures,
    isLoadingActions: areasQuery.isLoading || cellsQuery.isLoading || featuresQuery.isLoading,
    isActionsError: areasQuery.isError || cellsQuery.isError || featuresQuery.isError,
  };
};

const getAllOutputNamesStringsOfAction = (obj: any): string[] => {
  if (isPlainObject(obj)) {
    // If this object has output_name, collect it
    const current = obj.output_name ? [obj.output_name] : [];
    // Recursively collect from all values
    return [...current, ...flatMap(values(obj), getAllOutputNamesStringsOfAction)];
  }
  if (isArray(obj)) {
    // Recursively collect from all items
    return flatMap(obj, getAllOutputNamesStringsOfAction);
  }
  return [];
};

const getAllNewOptionValues = (actions: PostProcessingActionCreated[]) => {
  const newOptionValues: string[] = [];
  forEach(actions, (action) => {
    forEach(action.newOptionValues, (newOptionValue) => {
      if (!isEmpty(newOptionValue)) {
        newOptionValues.push(newOptionValue);
      }
    });
  });
  return uniq(newOptionValues);
};

const getOutputNames = (
  actions: PostProcessingActionCreated[],
  stain: string,
  classConfig: ClassConfigs,
  index: number,
  withFreeSoloValues: boolean = true
): Record<string, string[]> => {
  const outputNamesUntilIndex: Record<string, string[]> = {};
  const allNewOptionValuesList = getAllNewOptionValues(slice(actions, 0, index));
  for (let actionIndex = 0; actionIndex < index; actionIndex++) {
    const outputNames = getAllOutputNamesStringsOfAction(actions[actionIndex]);
    if (!isEmpty(outputNames) && actions[actionIndex]?.stain === stain) {
      const actionInput = actions[actionIndex]?.newOptionValueInput || actions[actionIndex]?.actionInput;

      forEach(outputNames, (outputName) => {
        // add tags to the output names if they exist
        if (!isEmpty(actions[actionIndex]?.action_params?.tags)) {
          outputName += `-${join(actions[actionIndex]?.action_params?.tags, '-')}`;
        }
        // add the output name if it is not new option value or withFreeSoloValues it's true
        // and it is not in the class config
        if (
          (withFreeSoloValues || !includes(allNewOptionValuesList, outputName)) &&
          !includes(classConfig?.[stain]?.[actionInput] as string[], outputName)
        ) {
          outputNamesUntilIndex[actionInput] = uniq(concat(outputNamesUntilIndex[actionInput] ?? [], outputName));
        }
      });
    }
  }

  return outputNamesUntilIndex;
};

const usePostProcessingActions = (
  stains: string[],
  classConfig: ClassConfigs,
  orchestrationMetadata: OrchestrationMetadata,
  newCellsCreated: Record<string, NewCell>,
  thresholds?: Thresholds,
  kMeansClusteringResults?: ClusteringResult[],
  binsModels?: string[],
  registeredAreaOptionsByTargetStain: Record<string, RegisteredAreaOption[]> = {},
  modelPreferencesByModelId: ModelPreferencesById = {},
  studyId?: string
) => {
  const { stainTypeOptions, stainTypeIdToDisplayName, isLoadingStainTypeOptions } =
    useStainTypeIdToDisplayName(studyId);
  const { areaTypeIdToDisplayName, isLoadingAreaTypes } = useAreaTypeIdToDisplayName();
  const { classificationModelOptions } = useClassificationModelOptions();

  const markerIdToDisplayName = (markerId?: string) => getProbeDisplayName(markerId, stainTypeIdToDisplayName);

  const getMarkerColumnDisplayName = (columnName: string) => {
    const markerColumnStart = columnName.indexOf('marker.');
    if (markerColumnStart === -1) {
      return startCase(columnName);
    }

    const markerColumn = columnName.slice(markerColumnStart);

    if (startsWith(markerColumn, markerPositivityColumnPrefix)) {
      const markerId = markerColumn.slice(markerPositivityColumnPrefix.length);
      return `${markerIdToDisplayName(markerId)} Positivity`;
    }

    if (startsWith(markerColumn, markerProbabilityColumnPrefix)) {
      const markerId = markerColumn.slice(markerProbabilityColumnPrefix.length);
      return `${markerIdToDisplayName(markerId)} Probability`;
    }

    const markerColumnParts = markerColumn.split('.');
    const markerId = markerColumnParts[1];
    const geometryIndex = findIndex(markerColumnParts, (part) => part === 'geometry');
    const quantificationIndex = findIndex(markerColumnParts, (part) => part === 'quant');
    const geometry = geometryIndex !== -1 ? markerColumnParts[geometryIndex + 1] : '';
    const quantification = quantificationIndex !== -1 ? markerColumnParts[quantificationIndex + 1] : '';

    if (markerId && quantification) {
      return join(
        compact([
          includes(markerColumnParts, BINNED) ? 'Binned' : null,
          markerIdToDisplayName(markerId),
          geometry ? startCase(geometry) : null,
          quantificationToDisplayName(quantification as 'od' | 'mean_intensity'),
        ]),
        ' '
      );
    }

    return startCase(markerColumn);
  };

  const getAttributeColumnDisplayName = (columnName: string) => {
    if (columnName === labelColumnName) {
      return startCase(labelColumnName);
    }

    return getMarkerColumnDisplayName(columnName);
  };

  const { featureParams: allFeatureParams } = useFeatureParams({
    enabled: !isLoadingStainTypeOptions && !isLoadingAreaTypes,
  });

  const featureParamsByType = useMemo(() => groupBy(allFeatureParams, 'type'), [allFeatureParams]);

  const { data: areaTypes } = useAreaTypes({ enabled: !isLoadingStainTypeOptions && !isLoadingAreaTypes });

  const { data: cellTypes } = useCellTypeOptions();
  const { data: taxonomyTags } = useTaxonomyTags();

  const getOutputNamesForAllStains = (
    index: number,
    actions: PostProcessingActionCreated[]
  ): Record<string, Record<string, string[]>> => {
    const outputNamesForAllStains: Record<string, Record<string, string[]>> = {};
    forEach(stains, (stain) => {
      outputNamesForAllStains[stain] = getOutputNames(actions, stain, classConfig, index, false);
    });

    return outputNamesForAllStains;
  };

  // return only the options that are common to all selected stains
  const getOnlyTheRepeatedOptions = <Option extends { id: string | number; label: string; input?: string }>(
    selectedStains: string[],
    options: Option[]
  ): Option[] => {
    if (selectedStains?.length === 1) {
      return options;
    }

    const grouped = groupBy(options, 'id');

    return uniq(
      flatMap(grouped, (group) => {
        if (size(group) === size(selectedStains)) {
          return group;
        }
        return [];
      })
    );
  };

  const defaultAreas = ['tissue', 'tissue_raw'];

  const getAreaOptions = (selectedStains: string[], options: ClassConfigs) => {
    const allAreasConfig = flatMap(
      map(selectedStains, (stain) => {
        return map(
          uniq([...(options?.[stain]?.area || []), ...(options?.[stain]?.tls || []), ...defaultAreas]),
          (area) => ({
            id: area as string,
            label: areaTypeIdToDisplayName(area as string),
          })
        );
      })
    );

    return getOnlyTheRepeatedOptions(selectedStains, allAreasConfig);
  };

  const getCellOptions = (selectedStains: string[], options: ClassConfigs) => {
    const allCellConfig = flatMap(
      map(selectedStains, (stain) => {
        return map(options?.[stain]?.cell, (cellId: string) => ({
          id: cellId,
          label: getFormattedCell(cellId),
        }));
      })
    );

    return getOnlyTheRepeatedOptions(selectedStains, allCellConfig);
  };

  const getCreatedCellsOptions = (selectedStains: string[]) => {
    if (size(selectedStains) > 1) {
      // if more than one stain is selected there is no way that the new cells exist in all selected stains
      return [];
    }

    return flatMap(
      map(selectedStains, (stain) => {
        return map(
          pickBy(newCellsCreated, (newCell) => newCell.stain === stain && !isEmpty(newCell.value)),
          (cell, cellId) => ({
            id: cellId,
            label: cell.name,
          })
        );
      })
    );
  };

  const getSpotDetectionProbeNames = (stain: string): string[] =>
    getSpotDetectionProbeNamesForStain(orchestrationMetadata, stain);

  const getSpotDetectionCountBinCells = (selectedStains: string[]): Record<string, NewCell> => {
    const countBinCells: Record<string, NewCell> = {};

    forEach(selectedStains, (stain) => {
      forEach(getSpotDetectionProbeNames(stain), (probeName) => {
        const setup = modelPreferencesByModelId[probeName] ?? modelPreferencesByModelId[SPOT_DETECTION_RESULT_NAME];
        forEach(
          getSpotDetectionCountBinCellDefinitions(probeName, setup, markerIdToDisplayName(probeName)),
          (definition) => {
            countBinCells[`${stain}:${definition.id}`] = {
              name: definition.label,
              stain,
              value: definition.condition,
            };
          }
        );
      });
    });

    return countBinCells;
  };

  const getSpotDetectionMappingData = (
    stain: string,
    actions: PostProcessingActionCreated[],
    index: number
  ): CellMapping[] => {
    const includesPositivity = some(
      slice(actions, 0, index),
      (action) =>
        action.actionName === INSTANCE_COUNT_POSITIVITY_ACTION_NAME &&
        includes(action.stain ? [action.stain] : action.stains, stain)
    );

    return uniqWith(
      flatMap(getSpotDetectionProbeNames(stain), (probeName) => {
        const setup = modelPreferencesByModelId[probeName] ?? modelPreferencesByModelId[SPOT_DETECTION_RESULT_NAME];
        return flatMap(getSpotDetectionCountColumns(probeName, setup, markerIdToDisplayName(probeName)), (column) => [
          {
            label: column.columnName,
            displayLabel: column.displayLabel,
            type: OrchestrationDataType.Int,
          },
          {
            label: `${column.columnName}.${BINNED}`,
            displayLabel: `${column.displayLabel} · Binned`,
            type: OrchestrationDataType.Int,
          },
          ...(includesPositivity
            ? [
                {
                  label: `${column.columnName}.pos`,
                  displayLabel: `${column.displayLabel} · Positivity`,
                  type: OrchestrationDataType.Boolean,
                },
              ]
            : []),
        ]);
      }),
      isEqual
    );
  };

  const getSpotDetectionBinnedColumnOptions = (selectedStains: string[]) =>
    getOnlyTheRepeatedOptions(
      selectedStains,
      flatMap(selectedStains, (stain) =>
        flatMap(getSpotDetectionProbeNames(stain), (probeName) => {
          const setup = modelPreferencesByModelId[probeName] ?? modelPreferencesByModelId[SPOT_DETECTION_RESULT_NAME];
          return map(getSpotDetectionCountColumns(probeName, setup, markerIdToDisplayName(probeName)), (column) => ({
            id: `${column.columnName}.${BINNED}`,
            label: `${column.displayLabel} · Binned`,
          }));
        })
      )
    );

  const getSpotDetectionThresholdOptions = (selectedStains: string[]) =>
    getOnlyTheRepeatedOptions(
      selectedStains,
      flatMap(selectedStains, (stain) =>
        flatMap(getSpotDetectionProbeNames(stain), (probeName) => {
          const setup = modelPreferencesByModelId[probeName] ?? modelPreferencesByModelId[SPOT_DETECTION_RESULT_NAME];
          return map(getSpotDetectionCountColumns(probeName, setup, markerIdToDisplayName(probeName)), (column) => ({
            id: column.columnName,
            label: column.displayLabel,
            instanceName: probeName,
            defaultThreshold: getSpotDetectionPositivityThresholdMinCount(setup),
          }));
        })
      )
    );

  const getInstanceFeatureCellFallbackOptions = (selectedStains: string[]) =>
    getOnlyTheRepeatedOptions(
      selectedStains,
      flatMap(selectedStains, (stain) =>
        !isEmpty(orchestrationMetadata?.[stain]?.[AttributesTable.CellTable]) ||
        !isEmpty(orchestrationMetadata?.[stain]?.[AttributesTable.DetectionsTable])
          ? [INSTANCE_FEATURE_CELL_OPTION]
          : []
      )
    );

  const getFinalCellOptions = (cellsOptions: any[]): any[] => {
    if (size(cellsOptions) === 0) {
      return [];
    }
    // add generic all_cells option
    return sortBy(uniqWith([allCellsOption, ...cellsOptions], isEqual), 'label');
  };

  const getBasicAttributesOptions = (selectedStains: string[], attributesTable: AttributesTable) => {
    return flatMap(
      map(selectedStains, (stain) => {
        const attributesOptionsFromClassConfig = map(
          pickBy(classConfig?.[stain], (classes) => !isEmpty(classes)),
          (classes, key) => {
            return {
              id: `${key}.${labelColumnName}`,
              input: key,
              label: getAttributeLabelDisplayName(key, labelColumnName),
              groupBy: getAttributeSourceDisplayName(key),
            };
          }
        );
        const attributesOptionsFromOrchestrationMetadata = compact(
          flatMap(orchestrationMetadata?.[stain]?.[attributesTable], (orchestrationData, key) => {
            return map(orchestrationData, (data) => {
              if (data.type === OrchestrationDataType.Float || data.type === OrchestrationDataType.Int) {
                return {
                  id: `${key}.${data.label}`,
                  label: getAttributeColumnDisplayName(data.label),
                  input: InputType.NUMBER,
                  groupBy: getAttributeSourceDisplayName(key),
                };
              } else if (data.type === OrchestrationDataType.Boolean) {
                return {
                  id: `${key}.${data.label}`,
                  label: getAttributeColumnDisplayName(data.label),
                  input: InputType.BOOLEAN,
                  groupBy: getAttributeSourceDisplayName(key),
                };
              }
              return null;
            });
          })
        );

        return concat(attributesOptionsFromClassConfig, attributesOptionsFromOrchestrationMetadata);
      })
    );
  };

  const getGeometryMetadataFromOrchestrationMetadata = (orchestrationDataByResultKey: {
    [resultKey: string]: OrchestrationData[];
  }) => {
    const geometryMetadata: { id: string; label: string }[] = [];

    forEach(orchestrationDataByResultKey, (orchestrationData, resultKey) => {
      forEach(orchestrationData, (data) => {
        if (data.type === OrchestrationDataType.Multipolygon) {
          geometryMetadata.push({
            id: `${resultKey}.${data.label}`,
            label: getAttributeColumnDisplayName(data.label),
          });
        }
      });
    });

    return geometryMetadata;
  };

  const getBinsFromOrchestrationMetadata = (selectedStains: string[], attributesTable: AttributesTable) => {
    const bins: { id: string; label: string }[] = [];
    forEach(selectedStains, (stain) => {
      forEach(orchestrationMetadata?.[stain]?.[attributesTable], (orchestrationData, resultKey) => {
        if (includes(binsModels, resultKey)) {
          forEach(orchestrationData, (data) => {
            if (data.type === OrchestrationDataType.Float) {
              bins.push({
                id: `${data.label}.${BINNED}`,
                label: getAttributeColumnDisplayName(`${data.label}.${BINNED}`),
              });
            }
          });
        }
      });
    });

    return bins;
  };

  const options: Record<
    string,
    (
      currentNewOptions?: Record<string, Record<string, string[]>>,
      selectedStains?: string[],
      inputSourceDependentOn?: string,
      actionDataForNewCell?: {
        actions: PostProcessingActionCreated[];
        index: number;
        postProcessingActionsById: Dictionary<PostProcessingAction>;
        actionInput: string;
      }
    ) => { id: string; label: string; input?: string }[]
  > = {
    [InputSource.STAIN_TYPES_OF_SELECTED_SLIDES]: () => {
      const stainOptions = map(stains, (stain) => ({
        id: stain,
        label: stainTypeIdToDisplayName(stain),
      }));

      return sortBy(stainOptions, 'label');
    },
    [InputSource.AREAS]: (currentNewOptions, selectedStains) => {
      const areaOptions = map(areaTypes, (areaType) => ({
        id: areaType.id,
        label: areaTypeIdToDisplayName(areaType.id),
      }));
      const newAreas = getAreaOptions(selectedStains, currentNewOptions);

      return sortBy(uniqWith([...areaOptions, ...newAreas], isEqual), 'label');
    },
    [InputSource.INTERMEDIATE_AREAS_PER_TARGET_STAIN]: (currentNewOptions, selectedStains) => {
      const areaConfigOptions = getAreaOptions(selectedStains, classConfig);
      const registeredAreaOptions = getOnlyTheRepeatedOptions(
        selectedStains,
        flatMap(selectedStains, (stain) =>
          map(registeredAreaOptionsByTargetStain[stain], ({ id, area, sourceStain }) => ({
            id,
            label: `${areaTypeIdToDisplayName(area)} from ${stainTypeIdToDisplayName(sourceStain)}`,
          }))
        )
      );
      const newAreas = getAreaOptions(selectedStains, currentNewOptions);

      return sortBy(uniqWith([...areaConfigOptions, ...registeredAreaOptions, ...newAreas], isEqual), 'label');
    },
    [InputSource.INTERMEDIATE_DETECTIONS_PER_TARGET_STAIN]: (_, selectedStains) => {
      const detectionLabelOptions = uniq(
        flatMap(selectedStains, (stain: string) => {
          const detectionsTableByResultKey = orchestrationMetadata?.[stain]?.[AttributesTable.DetectionsTable];
          return flatMap(values(detectionsTableByResultKey), getDetectionLabelOptions);
        })
      );

      return sortBy(uniqWith(detectionLabelOptions, isEqual), 'label');
    },
    [InputSource.CLASSES_CONFIG_AREAS_PER_TARGET_STAIN]: (_, selectedStains) => {
      const areaConfigOptions = getAreaOptions(selectedStains, classConfig);

      return sortBy(uniqWith(areaConfigOptions, isEqual), 'label');
    },
    [InputSource.CELLS]: (currentNewOptions, selectedStains) => {
      const cellOptions = map(filter(cellTypes, { isAbstract: false }), (cellType) => {
        return {
          id: cellType.shortId,
          label: getFormattedCell(cellType.shortId),
        };
      });
      const newCells = getCellOptions(selectedStains, currentNewOptions);

      return sortBy(uniqWith([...cellOptions, ...newCells], isEqual), 'label');
    },
    [InputSource.INTERMEDIATE_CELLS_PER_TARGET_STAIN]: (currentNewOptions, selectedStains) => {
      const cellConfigOptions = getCellOptions(selectedStains, classConfig);
      const newCells = getCellOptions(selectedStains, currentNewOptions);
      const cellsOption = [...cellConfigOptions, ...newCells];

      return getFinalCellOptions(cellsOption);
    },
    [InputSource.CLASSES_CONFIG_CELLS_PER_TARGET_STAIN]: (_, selectedStains) => {
      const cellConfigOptions = getCellOptions(selectedStains, classConfig);

      return getFinalCellOptions(cellConfigOptions);
    },
    [InputSource.INTERMEDIATE_CELLS_FEATURES_PER_TARGET_STAIN]: (
      currentNewOptions,
      selectedStains,
      _inputSourceDependentOn,
      actionDataForNewCell
    ) => {
      const cellConfigOptions = getCellOptions(selectedStains, classConfig);
      const newCells = getCellOptions(selectedStains, currentNewOptions);

      const newCellsCreatedInFeatures = getCreatedCellsOptions(selectedStains);
      const supportsSpotCountBins = includes(
        ['cells_count', 'cells_fraction'],
        actionDataForNewCell?.actions?.[actionDataForNewCell.index]?.actionName
      );
      const spotCountBinOptions = supportsSpotCountBins
        ? map(getSpotDetectionCountBinCells(selectedStains), (cell, id) => ({ id, label: cell.name }))
        : [];
      const cellsOption = [...cellConfigOptions, ...newCells, ...newCellsCreatedInFeatures, ...spotCountBinOptions];

      if (isEmpty(cellsOption)) {
        return getInstanceFeatureCellFallbackOptions(selectedStains);
      }

      return getFinalCellOptions(cellsOption);
    },
    [InputSource.TAXONOMY_TAGS]: (currentNewOptions, selectedStains, selectedCellTypeShortId) => {
      const selectedCellType = find(cellTypes, { shortId: selectedCellTypeShortId });
      const taxonomyTagsAvailableForSelectedCellType = filter(taxonomyTags, (taxonomyTag) =>
        some(
          taxonomyTag.labels,
          (label) => startsWith(selectedCellType?.id, `${label}.`) || label === selectedCellType?.id
        )
      );

      const taxonomyTagsOptions = map(taxonomyTagsAvailableForSelectedCellType, (taxonomyTag) => ({
        id: taxonomyTag.id,
        label: taxonomyTag.displayName,
      }));

      return sortBy(taxonomyTagsOptions, 'label');
    },
    [InputSource.CELL_ATTRIBUTES_OF_SELECTED_SLIDES]: (
      currentNewOptions,
      selectedStains,
      inputSourceDependentOn,
      actionDataForNewCell
    ) => {
      const attributesOptions = getBasicAttributesOptions(selectedStains, AttributesTable.CellTable);
      const additionalOptionsPerStain: Record<
        string,
        {
          id: string;
          label: string;
          input?: string;
          groupBy?: string;
        }[]
      > = {};

      forEach(selectedStains, (stain) => {
        forEach(
          getCellNewMappingData(
            actionDataForNewCell.actions,
            stain,
            actionDataForNewCell.index,
            actionDataForNewCell.postProcessingActionsById,
            actionDataForNewCell.actionInput
          ),
          (data) => {
            additionalOptionsPerStain[stain] = concat(additionalOptionsPerStain[stain] || [], {
              id: data?.label,
              label: data?.displayLabel ?? data?.label,
              input: data?.type,
              groupBy: getAttributeSourceDisplayName('cell'),
            });
          }
        );
      });

      const sharedAdditionalOptions = getOnlyTheRepeatedOptions(
        selectedStains,
        flatMap(additionalOptionsPerStain, (additionalOptions) => additionalOptions)
      );

      return sortBy(uniqWith(concat(attributesOptions, sharedAdditionalOptions), isEqual), ['groupBy', 'label']);
    },
    [InputSource.AREA_ATTRIBUTES_OF_SELECTED_SLIDES]: (currentNewOptions, selectedStains) => {
      const attributesOptions = getBasicAttributesOptions(selectedStains, AttributesTable.AreaTable);

      return sortBy(uniqWith(attributesOptions, isEqual), ['groupBy', 'label']);
    },
    [InputSource.CELL_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT]: (
      currentNewOptions,
      selectedStains,
      inputSourceDependentOn,
      actionDataForNewCell
    ) => {
      const attributesOptions = getBasicAttributesOptions(selectedStains, AttributesTable.CellTable);

      const additionalOptionsPerStain: Record<
        string,
        {
          id: string;
          label: string;
          input?: string;
          groupBy?: string;
        }[]
      > = {};

      // work around to reassign marker pos
      forEach(selectedStains, (stain) => {
        forEach(
          uniqWith(
            [
              ...getCellNewMappingData(
                actionDataForNewCell.actions,
                stain,
                actionDataForNewCell.index,
                actionDataForNewCell.postProcessingActionsById,
                actionDataForNewCell.actionInput
              ),
              ...filter(
                getSpotDetectionMappingData(stain, actionDataForNewCell.actions, actionDataForNewCell.index),
                (data) => data.type === OrchestrationDataType.Boolean && endsWith(data.label, '.pos')
              ),
            ],
            isEqual
          ),
          (data) => {
            if (startsWith(data?.label, 'marker.pos.') || endsWith(data?.label, '.pos')) {
              additionalOptionsPerStain[stain] = concat(additionalOptionsPerStain[stain] || [], {
                id: data?.label,
                label: data?.displayLabel ?? data?.label,
                input: InputType.BOOLEAN,
                groupBy: getAttributeSourceDisplayName('cell'),
              });
            }
          }
        );
      });

      const sharedAdditionalOptions = getOnlyTheRepeatedOptions(
        selectedStains,
        flatMap(additionalOptionsPerStain, (additionalOptions) => additionalOptions)
      );

      return sortBy(uniqWith(concat(attributesOptions, sharedAdditionalOptions), isEqual), ['groupBy', 'label']);
    },
    [InputSource.AREA_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT]: (currentNewOptions, selectedStains) => {
      const attributesOptions = getBasicAttributesOptions(selectedStains, AttributesTable.AreaTable);

      return sortBy(uniqWith(attributesOptions, isEqual), ['groupBy', 'label']);
    },
    [InputSource.OPTIONS_OF_SELECTED_ATTRIBUTE]: (currentNewOptions, selectedStains, attribute) => {
      const optionsByAttribute: Record<string, { id: string; label: string }[]> = {
        ['cell.label']: options[InputSource.CELLS](currentNewOptions, selectedStains),
        ['area.label']: options[InputSource.AREAS](currentNewOptions, selectedStains),
      };

      return optionsByAttribute[attribute] || [];
    },
    [InputSource.CELL_GEOMETRY_METADATA]: (_, selectedStains) => {
      const geometryMetadata: { id: string; label: string }[] = [];

      forEach(selectedStains, (stain) => {
        geometryMetadata.push(
          ...getGeometryMetadataFromOrchestrationMetadata(orchestrationMetadata?.[stain]?.[AttributesTable.CellTable])
        );

        // build the mapping filters metadata from the classes config only for nucleai segmentation
        if (classConfig?.[stain]?.[modelTypeNucleiSegmentation.value]) {
          geometryMetadata.push({
            id: `${modelTypeNucleiSegmentation.value}.geometry`,
            label: `${modelTypeNucleiSegmentation.value}.geometry`,
          });
        }

        // not sure this code is needed anymore
        // BinaryClassifier is not supposed to have classes config
        // build the mapping filters metadata from the classes config only for binary classifier
        if (classConfig?.[stain]?.[modelTypeBinaryClassifier.value]) {
          geometryMetadata.push({
            id: `${modelTypeBinaryClassifier.value}.nuclear_geometry`,
            label: `${modelTypeBinaryClassifier.value}.nuclear_geometry`,
          });
        }
      });

      return getOnlyTheRepeatedOptions(selectedStains, geometryMetadata);
    },
    [InputSource.AREA_GEOMETRY_METADATA]: (_, selectedStains) => {
      const geometryMetadata: { id: string; label: string }[] = [];

      forEach(selectedStains, (stain) => {
        geometryMetadata.push(
          ...getGeometryMetadataFromOrchestrationMetadata(orchestrationMetadata?.[stain]?.[AttributesTable.AreaTable])
        );
      });

      return getOnlyTheRepeatedOptions(selectedStains, geometryMetadata);
    },
    [InputSource.INSTANCE_COUNT_THRESHOLDS]: (_, selectedStains) => getSpotDetectionThresholdOptions(selectedStains),
    [InputSource.CELL_BINS]: (_, selectedStains) => {
      const quantificationBins: { id: string; label: string }[] = [];
      quantificationBins.push(...getBinsFromOrchestrationMetadata(selectedStains, AttributesTable.CellTable));
      quantificationBins.push(...getSpotDetectionBinnedColumnOptions(selectedStains));

      // check if one of the binsModels is classificationModelOptions and if so add to quantificationBins
      forEach(classificationModelOptions, (model) => {
        if (includes(binsModels, model.id)) {
          quantificationBins.push({
            id: `${model.id}.${BINNED}`,
            label: `${model.displayName}.${BINNED}`,
          });
        }
      });

      return getOnlyTheRepeatedOptions(selectedStains, quantificationBins);
    },
    [InputSource.AREA_BINS]: (_, selectedStains) => {
      const quantificationBins: { id: string; label: string }[] = getBinsFromOrchestrationMetadata(
        selectedStains,
        AttributesTable.AreaTable
      );

      return getOnlyTheRepeatedOptions(selectedStains, quantificationBins);
    },
  };

  const newCellColumnsOptions: Record<string, () => { id: string; label: string; input?: string }[]> = {
    [NewCellColumns.THRESHOLD_RESULT_MARKERS]: () =>
      map(uniq(keys(thresholds)), (marker) => ({
        id: marker,
        label: markerIdToDisplayName(marker),
        groupBy: getAttributeSourceDisplayName('cell'),
      })),
    [NewCellColumns.K_AND_N_NEIGHBOR]: () => {
      const kMeansClasses = getKmeansCellLabelColumns(kMeansClusteringResults);
      return map(kMeansClasses, (kMeansClass) => ({ id: kMeansClass, label: kMeansClass, groupBy: 'cell' }));
    },
  };

  // add feature params by type to options
  forEach(featureParamsByType, (featureParams, featureParamType) => {
    options[featureParamType] = () =>
      map(featureParams, (featureParam) => ({
        id: featureParam.id,
        label: featureParam.displayName,
      }));
  });

  const getOptions = (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    inputSourceDependentOn?: string
  ) => {
    if (isArray(optionSource)) {
      return map(optionSource, (option) => ({
        id: option,
        label: startCase(option),
      }));
    } else {
      const getOptionsFunction = options[optionSource];
      if (!getOptionsFunction) {
        return [];
      }

      const outputNamesPerStain: Record<string, Record<string, string[]>> = {};

      forEach(selectedStains, (stain) => {
        set(
          outputNamesPerStain,
          stain,
          getOutputNames(actions, stain, classConfig, index, actionInput !== ActionInput.FEATURE_FAMILY)
        );
      });

      return getOptionsFunction(outputNamesPerStain, selectedStains, inputSourceDependentOn, {
        actions,
        index,
        postProcessingActionsById,
        actionInput,
      });
    }
  };

  const getOptionsByValue = (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    inputSourceDependentOn?: string
  ) => {
    return keyBy(
      getOptions(
        optionSource,
        selectedStains,
        index,
        actions,
        actionInput,
        postProcessingActionsById,
        inputSourceDependentOn
      ),
      'id'
    );
  };

  const getCellNewMappingData = (
    actions: PostProcessingActionCreated[],
    stain: string,
    index: number,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    actionInput: string
  ): CellMapping[] => {
    const newCellNewMappingData: CellMapping[] = [];

    for (let actionIndex = 0; actionIndex < index; actionIndex++) {
      const postProcessingAction = postProcessingActionsById[actions[actionIndex]?.actionId];
      if (!isEmpty(postProcessingAction?.newCellColumns) && actions[actionIndex]?.stain === stain) {
        const labels: string[][] = [];
        forEach(postProcessingAction.newCellColumns, (newCellColumnPart) => {
          // for each part of the new cell, we take the first matching value(s) in the following order:
          // 1. The inputKey value
          // 2. The input source function return values
          // 3. The special new cell column function return values
          // 4. The string itself

          const newCellColumnInput = find(postProcessingAction.inputs, { inputKey: newCellColumnPart });

          // the newCellColumn is inputKey
          if (!isEmpty(newCellColumnInput)) {
            const newCellColumnInputValue = get(actions[actionIndex], newCellColumnPart);
            if (!isEmpty(newCellColumnInputValue) || isNumber(newCellColumnInputValue)) {
              labels.push(flatMap([newCellColumnInputValue]));
            }
          } else if (includes(keys(options), newCellColumnPart)) {
            const getOptionsFunction = options[newCellColumnPart];

            const outputNamesPerStain: Record<string, Record<string, string[]>> = {};
            set(
              outputNamesPerStain,
              stain,
              getOutputNames(actions, stain, classConfig, actionIndex, actionInput !== ActionInput.FEATURE_FAMILY)
            );

            const classesOptions = getOptionsFunction(outputNamesPerStain, [stain]);
            if (!isEmpty(classesOptions)) {
              labels.push(map(classesOptions, 'id'));
            }
          } else if (includes(keys(newCellColumnsOptions), newCellColumnPart)) {
            const getNewCellColumnOptionsFunction = newCellColumnsOptions[newCellColumnPart];

            const newCellColumnOptions = getNewCellColumnOptionsFunction();
            labels.push(map(newCellColumnOptions, 'id'));
          } else {
            // simple string
            labels.push([newCellColumnPart]);
          }
        });

        if (!isEmpty(labels)) {
          forEach(combineArrays(labels), (label) => {
            newCellNewMappingData.push({
              label,
              displayLabel: getAttributeColumnDisplayName(label),
              type: OrchestrationDataType.Float,
            });
          });
        }
      }
    }

    return sortBy(uniqWith(newCellNewMappingData, isEqual), 'label');
  };

  const getMappingFiltersMetadataForLogicalQuery = (
    stain: string,
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    actionAttributesTable: AttributesTable
  ) => {
    const optionsKeyByKey: Record<string, string> = {
      cell: InputSource.INTERMEDIATE_CELLS_PER_TARGET_STAIN,
      area: InputSource.INTERMEDIATE_AREAS_PER_TARGET_STAIN,
    };

    const mappingFiltersMetadata: MappingFilterMetadata[] = [];

    // build the mapping filters metadata from the classes config
    forEach(classConfig?.[stain], (classes, key) => {
      // if the key is nuclei segmentation we don't want to show the classes
      if (!isEmpty(classes) && key !== modelTypeNucleiSegmentation.value) {
        if (
          actionAttributesTable === AttributesTable.CellTable ||
          (actionAttributesTable === AttributesTable.AreaTable && key === 'area')
        ) {
          let classesOptions = map(classes, (classOption: string | number) => ({
            id: classOption,
            label: classOption,
          }));

          const getOptionsFunction = options[optionsKeyByKey[key]];
          if (getOptionsFunction) {
            const outputNamesPerStain: Record<string, Record<string, string[]>> = {};
            set(
              outputNamesPerStain,
              stain,
              getOutputNames(actions, stain, classConfig, index, actionInput !== ActionInput.FEATURE_FAMILY)
            );

            classesOptions = getOptionsFunction(outputNamesPerStain, [stain]);
          }

          mappingFiltersMetadata.push({
            sourceName: key,
            sourceDisplayName: getAttributeSourceDisplayName(key),
            data: [
              {
                label: labelColumnName,
                displayLabel: startCase(labelColumnName),
                options: classesOptions,
                type: OrchestrationDataType.Categorical,
              },
            ],
          });
        }
      }
    });

    // build the mapping filters metadata for the new cell columns only for cell attributes table
    if (actionAttributesTable === AttributesTable.CellTable) {
      const cellMappingData = uniqWith(
        [
          ...getSpotDetectionMappingData(stain, actions, index),
          ...getCellNewMappingData(actions, stain, index, postProcessingActionsById, actionInput),
        ],
        isEqual
      );

      if (!isEmpty(cellMappingData)) {
        const cellMappingFiltersMetadataIndex = findIndex(mappingFiltersMetadata, { sourceName: 'cell' });
        if (cellMappingFiltersMetadataIndex !== -1) {
          mappingFiltersMetadata[cellMappingFiltersMetadataIndex].data = [
            ...mappingFiltersMetadata[cellMappingFiltersMetadataIndex].data,
            ...cellMappingData,
          ];
        } else {
          mappingFiltersMetadata.push({
            sourceName: 'cell',
            sourceDisplayName: getAttributeSourceDisplayName('cell'),
            data: cellMappingData,
          });
        }
      }
    }

    // build the mapping filters metadata from the orchestration metadata
    const relevantOrchestrationMetadata = orchestrationMetadata?.[stain]?.[actionAttributesTable];

    forEach(relevantOrchestrationMetadata, (orchestrationData, key) => {
      mappingFiltersMetadata.push({
        sourceName: key,
        sourceDisplayName: getAttributeSourceDisplayName(key),
        data: map(
          omitBy(orchestrationData, (data) =>
            includes([OrchestrationDataType.Point, OrchestrationDataType.Multipolygon], data.type)
          ),
          (data) => {
            const dataOptions =
              data.type === OrchestrationDataType.Categorical
                ? map(data.options, (option: string) => ({
                    id: option,
                    label: option,
                  }))
                : (data.options as NumberOption);

            return {
              label: data.label,
              displayLabel: getAttributeColumnDisplayName(data.label),
              options: dataOptions,
              type: data.type,
            };
          }
        ),
      });
    });

    return mappingFiltersMetadata;
  };

  return {
    getOptions,
    getOptionsByValue,
    getOutputNamesForAllStains,
    getMappingFiltersMetadataForLogicalQuery,
    spotDetectionCountBinCells: getSpotDetectionCountBinCells(stains),
    stainTypeOptions,
  };
};

export default usePostProcessingActions;

const allCellsId = '__all_cells__';

export const allCellsOption = {
  id: allCellsId,
  label: getFormattedCell(allCellsId),
};

export const getKmeansCellLabelColumns = (kMeansClusteringResults: ClusteringResult[]) => {
  const approvedKMeans = filter(kMeansClusteringResults, (kMeans) => {
    return kMeans.internallyApproved;
  });

  const kMeansClasses: string[] = [];
  forEach(approvedKMeans, (kMeans) => {
    const k = kMeans.kMeansParams.k;
    const nNeighbors = kMeans.neighborCountParams.nNeighbors;
    kMeansClasses.push(`k.${k}.n_neighbors.${nNeighbors}.cluster_id`);
  });
  return uniq(kMeansClasses);
};
